import React, { useCallback, useReducer } from 'react'
import { useTranslation } from 'react-i18next'
import { useCookies } from 'react-cookie'
import classes from './ThemeChanger.scss'
import { useBrowserTheme, useThemeChanger } from 'cityads-v3/hooks/theme'

import IconButton from '@mui/material/IconButton'
import Brightness2OutlinedIcon from '@mui/icons-material/Brightness2Outlined'
import Brightness6OutlinedIcon from '@mui/icons-material/Brightness6Outlined'
import Tooltip from 'cityads-v3/components/Tooltip/Tooltip'
import WbSunnyOutlinedIcon from '@mui/icons-material/WbSunnyOutlined'

type ThemeChangerProps = {
  onChange?: (themeName: string) => void
}

/**
 * Переключатель режима светлой/системной/тёмной темы
 *
 * Note: refresh нужен для вызова рендера, т.к. параметр isSystem
 * читается из localStorage и не имеет слушателя изменений.
 * Без принудительного вызова рендера переход с системной
 * темы на черную или белую с той же самой активной - будет
 * применяться, но индикатор не будет перерисывываться.
 */
export default function ThemeChanger({ onChange }: ThemeChangerProps): JSX.Element {
  const { t } = useTranslation(['main'])
  const [, setCookie] = useCookies(['application_theme'])
  const { changeTheme, themeName } = useThemeChanger()
  const [, refresh] = useReducer(num => num + 1, 0)
  const browserTheme = useBrowserTheme()
  const isSystem = window?.localStorage?.theme === 'system';

  const makeLight = useCallback(() => {
    changeTheme('light')
    onChange?.('light')
    setCookie('application_theme', 'light')
    refresh()
  }, [changeTheme, refresh, onChange, setCookie])

  const makeDark = useCallback(() => {
    changeTheme('dark')
    onChange?.('dark')
    setCookie('application_theme', 'dark')
    refresh()
  }, [changeTheme, refresh, onChange, setCookie])

  const makeSystem = useCallback(() => {
    changeTheme(browserTheme, true)
    onChange?.(browserTheme)
    setCookie('application_theme', 'system')
    refresh()
  }, [browserTheme, changeTheme, refresh, onChange, setCookie])

  return (
    <div className={classes.container}>
      <div>
        <Tooltip isLight={themeName === 'dark'} title={t('theme.light')}>
          <IconButton size="large" onClick={makeLight}>
            <WbSunnyOutlinedIcon color={!isSystem && themeName === 'light' ? 'primary' : undefined} />
          </IconButton>
        </Tooltip>
      </div>
      <div>
        <Tooltip isLight={themeName === 'dark'} title={t('theme.system')}>
          <IconButton size="large" onClick={makeSystem}>
            <Brightness6OutlinedIcon color={isSystem ? 'primary' : undefined} />
          </IconButton>
        </Tooltip>
      </div>
      <div>
        <Tooltip isLight={themeName === 'dark'} title={t('theme.dark')}>
          <IconButton size="large" onClick={makeDark}>
            <Brightness2OutlinedIcon color={!isSystem && themeName === 'dark' ? 'primary' : undefined} />
          </IconButton>
        </Tooltip>
      </div>
    </div>
  )
}
